/**
 * The dated-imagery layer and the run's marks on it — the second and third concepts of
 * docs/MAP_IMAGERY_RESEARCH.md §2. The first, the basemap, is not here at all: it is always
 * on and needs neither this hook nor the backend (map/basemap.ts).
 *
 * WHAT CHANGED. Imagery used to require a field: the only endpoint was
 * `/api/fields/{id}/tiles/{date}` and the only dates were the ones a finished run had
 * scored. So a map with nothing drawn on it had no picture at all, and a field without a run
 * had no dates even though the satellites had flown over it all year. Now:
 *
 *   dates    = every overpass over the VIEWPORT (hooks/useViewportDates.ts), plus the run's
 *              own dates, which include the rejected ones the viewport list filters out;
 *   marks    = what the run did with each of them, straight out of `optical_obs` — accepted,
 *              rejected with a reason, or never scored. No Earth Engine at view time;
 *   tiles    = the field's clipped preview when a field is selected, the viewport box
 *              otherwise. Both answers carry a bbox, and the map declares it as `bounds`.
 *
 * WHY THE FIELD ENDPOINT IS KEPT FOR A SELECTED FIELD. It is strictly the cheaper of the
 * two: the image is clipped to the polygon rather than to the screen, so the browser asks
 * for a handful of tiles instead of a screenful, and every tile it does not ask for is an
 * Earth Engine render nobody pays for. "A field is a smaller box than the screen" is the
 * whole rule.
 *
 * Nothing here fetches imagery until somebody asks for it: opening a field, picking a date
 * with the layer off, and panning the map with the layer off all cost zero GEE calls.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import * as api from "../api/client";
import { ApiError } from "../api/client";
import type { PreviewDate, Run } from "../api/types";
import type { PreviewStatus } from "../components/PreviewControl";
import { buildCalendar, dayOf, type CalendarModel, type RunWindow } from "../lib/calendar";
import { PIPELINE_LOOKBACK_DAYS } from "../lib/constants";
import { shiftIsoDate } from "../lib/format";
import { TILES_TTL_MS, TtlCache } from "../lib/ttlCache";
import { tileBoxOf, type SkipReason, type TileBox, type Viewport } from "../lib/viewport";
import { asPreviewBounds, type PreviewBounds } from "../map/preview";
import { useViewportDates } from "./useViewportDates";

interface TileResult {
  tileUrl: string;
  bounds: PreviewBounds | null;
}

/**
 * Стрелка при выключенном слое теперь его включает, а зажатая клавиша — это десять смен
 * даты за две секунды. Каждая из них стоила бы своего `getMapId`; с задержкой платит только
 * та дата, на которой человек остановился.
 */
export const TILE_REQUEST_DEBOUNCE_MS = 300;

/** Mirrors the server's 2 h template cache: stepping back to a date already seen is free. */
const tileCache = new TtlCache<TileResult>(TILES_TTL_MS);

export const resetPreviewTileCache = (): void => tileCache.clear();

const datesFromRun = (run: Run | null): PreviewDate[] => {
  const audit = run?.payload?.optical_audit ?? [];
  const seen = new Map<string, PreviewDate>();
  for (const row of audit) {
    if (!row.date) continue;
    const existing = seen.get(row.date);
    // Accepted wins the slot: it is the row worth previewing.
    if (!existing || (!existing.accepted && row.accepted)) {
      seen.set(row.date, {
        date: row.date,
        accepted: Boolean(row.accepted),
        reject_reason: row.rejection_reason ?? null,
        ndvi_mean: row.ndvi_mean ?? null,
      });
    }
  }
  return [...seen.values()].sort((a, b) => a.date.localeCompare(b.date));
};

export const usePreview = (
  fieldId: number | null,
  run: Run | null,
  viewport: Viewport | null = null,
) => {
  const [dates, setDates] = useState<PreviewDate[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  /**
   * ФИЛЬТР «ТОЛЬКО ПРИНЯТЫЕ» ВЫКЛЮЧЕН ВСЕГДА, А НЕ ТОЛЬКО ДО ПРОГОНА (решение владельца
   * 2026-10-02: «надо при анализе поля всегда показывать актуальные снимки ВСЕ»).
   *
   * Он и раньше начинался выключенным, но включался САМ, как только у поля появлялся прогон, —
   * то есть ровно на тех полях, которые смотрят. Стрелки после анализа шагали по принятым
   * датам, а остальные снимки оставались видны только в календаре: человек, листавший даты,
   * не знал, что часть съёмок от него спрятана, и «все актуальные снимки» были неправдой.
   *
   * Фильтр остался — галочкой в календаре, где рядом видно, сколько дат он убирает. Это выбор
   * человека, а не наше решение за него: отбраковка — про расчёт, а не про право смотреть.
   */
  const [acceptedOnly, setAcceptedOnly] = useState(false);
  const [tileUrl, setTileUrl] = useState<string | null>(null);
  // The bbox as the response reports it: what keeps the map from asking for tiles outside
  // the picture (src/map/preview.ts).
  const [bounds, setBounds] = useState<PreviewBounds | null>(null);
  const [status, setStatus] = useState<PreviewStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  /** Developer surface only: how often the client cache answered instead of Earth Engine. */
  const [cacheHits, setCacheHits] = useState(0);

  // Viewport dates are fetched while the layer is on OR the calendar is open — both are a
  // person asking "what pictures exist here", and neither happens by itself. Named rather
  // than inlined because the control has to know it: an empty list nobody asked for must
  // read as an invitation, not as "нет съёмок".
  const datesActive = enabled || calendarOpen;
  const viewportDates = useViewportDates(viewport, datesActive);

  useEffect(() => {
    if (fieldId === null) {
      setDates([]);
      setSelectedDate(null);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const loaded = await api.listPreviewDates(fieldId);
        if (!alive) return;
        setDates(loaded.length > 0 ? loaded : datesFromRun(run));
      } catch (loadError) {
        if (!alive) return;
        if (loadError instanceof ApiError && loadError.isMissingEndpoint)
          setDates(datesFromRun(run));
        else setDates(datesFromRun(run));
      }
    })();
    return () => {
      alive = false;
    };
  }, [fieldId, run]);

  // The run's own window, so the calendar can dim what the run never looked at. Same two
  // dates the state strip spans, taken from the same payload (lib/strip.ts).
  const runWindow: RunWindow | null = useMemo(() => {
    if (!run) return null;
    const end = run.analysis_date;
    const start =
      run.payload?.window_start ??
      run.payload?.optical_state?.window_start ??
      shiftIsoDate(end, -PIPELINE_LOOKBACK_DAYS);
    return { start, end };
  }, [run]);

  const calendar: CalendarModel = useMemo(
    () => buildCalendar(viewportDates.dates, dates, runWindow),
    [viewportDates.dates, dates, runWindow],
  );

  useEffect(() => {
    // Default to the freshest accepted date: that is the one a "ВОЗМОЖНО" needs eyes on.
    if (selectedDate !== null || dates.length === 0) return;
    const accepted = dates.filter((item) => item.accepted);
    const pick = (accepted.length > 0 ? accepted : dates).at(-1);
    if (pick) setSelectedDate(pick.date);
  }, [dates, selectedDate]);

  useEffect(() => {
    // No field and no run: the freshest overpass over the screen is the sensible default.
    if (selectedDate !== null || dates.length > 0 || calendar.days.length === 0) return;
    setSelectedDate(calendar.days[calendar.days.length - 1]?.date ?? null);
  }, [calendar, dates.length, selectedDate]);

  // THE TILE BOX, and why it is not `viewportDates.bbox` any more. That bbox only moves when
  // the dates are re-fetched — once per 30 km — while a z14 viewport is 11 km wide. One
  // screen of panning left the raster's `bounds` behind the view, every tile was correctly
  // suppressed, and the picture vanished with the layer still on. Two jobs, two boxes
  // (lib/viewport.ts); the four cost guards on the dates are untouched.
  const tileBox: TileBox | null = useMemo(() => tileBoxOf(viewport), [viewport]);
  const tileBboxParam = tileBox?.param ?? null;

  useEffect(() => {
    if (!enabled || !selectedDate) {
      setTileUrl(null);
      setBounds(null);
      setStatus("idle");
      return;
    }
    // A field is the smaller box; without one we fall back to the screen, and without a
    // screen bbox (below zoom 14) there is nothing to ask for — and asking for the whole
    // world is exactly the spend this avoids.
    const key =
      fieldId !== null
        ? `field:${fieldId}:${selectedDate}`
        : `bbox:${tileBboxParam}:${selectedDate}`;
    if (fieldId === null && !tileBboxParam) {
      setTileUrl(null);
      setBounds(null);
      setStatus("idle");
      return;
    }

    const hit = tileCache.get(key);
    if (hit) {
      // A template we already hold costs nothing, so it is shown at once: the delay below
      // exists to skip UNMADE requests, not to make cheap ones feel slow.
      setTileUrl(hit.tileUrl);
      setBounds(hit.bounds);
      setStatus("ready");
      setError(null);
      setCacheHits((count) => count + 1);
      return;
    }

    let alive = true;
    setStatus("loading");
    setError(null);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result: TileResult =
            fieldId !== null
              ? await api.getPreviewTiles(fieldId, selectedDate).then((tiles) => ({
                  tileUrl: tiles.tile_url,
                  bounds: asPreviewBounds(tiles.bounds),
                }))
              : await api
                  .getSatelliteTiles(selectedDate, tileBboxParam as string)
                  .then((tiles) => ({
                    tileUrl: tiles.tile_url,
                    bounds: asPreviewBounds(tiles.bounds),
                  }));
          if (!alive) return;
          tileCache.put(key, result);
          setTileUrl(result.tileUrl);
          setBounds(result.bounds);
          setStatus("ready");
        } catch (tileError) {
          if (!alive) return;
          setTileUrl(null);
          setBounds(null);
          setStatus("error");
          setError(tileError instanceof Error ? tileError.message : String(tileError));
        }
      })();
    }, TILE_REQUEST_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [enabled, fieldId, selectedDate, tileBboxParam]);

  // WHY THE PICTURE IS NOT BEING ASKED FOR WHILE THE LAYER IS ON.
  // Without a field the tile box is the screen, and below zoom 14 `tileBoxOf` returns null on
  // purpose — that is the cheapest cost guard there is. The effect above then goes quietly to
  // `idle`: no request, no error, no picture, and the control went on saying the layer was
  // shown. A guard the user cannot see is indistinguishable from a broken control, so the
  // reason comes out of the hook the same way `datesSkipped` already does.
  const tilesSkipped: SkipReason =
    enabled && selectedDate !== null && fieldId === null && viewport !== null && tileBox === null
      ? "zoom"
      : null;

  /** Clicking a calendar day or a strip column: show that date, switching the overlay on. */
  const showDate = useCallback((date: string) => {
    setSelectedDate(date);
    setEnabled(true);
  }, []);

  return {
    dates,
    calendar,
    selectedDay: dayOf(calendar, selectedDate),
    selectedDate,
    setSelectedDate,
    enabled,
    setEnabled,
    calendarOpen,
    setCalendarOpen,
    acceptedOnly,
    setAcceptedOnly,
    tileUrl,
    bounds,
    status,
    error,
    showDate,
    datesLoading: viewportDates.loading,
    datesError: viewportDates.error,
    datesSkipped: viewportDates.skipped,
    /** Which guard is keeping the picture off the map while the layer is on. */
    tilesSkipped,
    /** Спрашивали ли даты вообще. Пустой список до вопроса — не ответ «их нет». */
    datesActive,
    /** The two boxes, kept apart on purpose — and shown apart on the developer surface. */
    datesBbox: viewportDates.bbox,
    tileBox,
    tileCacheHits: cacheHits,
    /** Whether the picture on screen is clipped to the field or to the viewport box. */
    clippedToField: fieldId !== null,
  };
};
